import type { TFunction } from "i18next";

type ValidationDetailItem = {
  loc?: unknown;
  msg?: unknown;
  detail?: unknown;
  message?: unknown;
  error?: unknown;
};

const KNOWN_ERROR_I18N_KEYS: Record<string, string> = {
  account_suspended: "errors.accountSuspended",
  wrong_workspace: "errors.wrongWorkspace",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const normalizePath = (value: unknown): string => {
  if (!Array.isArray(value)) return "";

  const parts = value
    .map((segment) => (typeof segment === "string" || typeof segment === "number" ? String(segment) : ""))
    .filter(Boolean)
    .filter((segment) => segment !== "body" && segment !== "query" && segment !== "path");

  return parts.join(".");
};

const normalizeDetailItem = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (!isRecord(value)) return null;

  const item = value as ValidationDetailItem;

  if (typeof item.detail === "string") return item.detail;
  if (typeof item.message === "string") return item.message;
  if (typeof item.error === "string") return item.error;

  if (typeof item.msg === "string") {
    const path = normalizePath(item.loc);
    return path ? `${path}: ${item.msg}` : item.msg;
  }

  return null;
};

export const normalizeApiErrorDetail = (
  detail: unknown,
  fallback: string | null = null,
): string | null => {
  if (typeof detail === "string") {
    const trimmed = detail.trim();
    if (trimmed === "account_suspended") {
      return "account_suspended";
    }
    if (trimmed.toLowerCase().includes("wrong workspace")) {
      return "wrong_workspace";
    }
    return trimmed || fallback;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map(normalizeDetailItem)
      .filter((value): value is string => Boolean(value?.trim()));

    if (messages.length > 0) {
      return Array.from(new Set(messages)).join("; ");
    }

    return fallback;
  }

  if (isRecord(detail)) {
    const record = detail as ValidationDetailItem;

    if (typeof record.detail === "string") return record.detail;
    if (Array.isArray(record.detail)) return normalizeApiErrorDetail(record.detail, fallback);
    if (typeof record.message === "string") return record.message;
    if (typeof record.error === "string") return record.error;

    const normalizedItem = normalizeDetailItem(record);
    if (normalizedItem) return normalizedItem;
  }

  return fallback;
};

const readApiErrorCode = (detail: Record<string, unknown>): string | undefined => {
  if (typeof detail.error_code === "string") return detail.error_code;
  const payload = detail.detailPayload;
  if (isRecord(payload) && typeof payload.error_code === "string") {
    return payload.error_code;
  }
  return undefined;
};

export const isInsufficientCreditsError = (detail: unknown): boolean => {
  if (!isRecord(detail)) return false;
  return readApiErrorCode(detail) === "INSUFFICIENT_CREDITS";
};

export const insufficientCreditsMessage = (detail: unknown): string => {
  if (isInsufficientCreditsError(detail)) {
    return "Your company has run out of credits. Open billing to subscribe or add credits.";
  }
  return normalizeApiErrorDetail(detail, "Insufficient credits.") ?? "Insufficient credits.";
};

export const isStorageLimitExceededError = (detail: unknown): boolean => {
  if (!isRecord(detail)) return false;
  return readApiErrorCode(detail) === "STORAGE_LIMIT_EXCEEDED";
};

export const storageLimitExceededMessage = (detail: unknown): string => {
  if (isStorageLimitExceededError(detail)) {
    return "Your company has reached its storage limit. Free up space or upgrade your plan to upload more files.";
  }
  return normalizeApiErrorDetail(detail, "Storage limit exceeded.") ?? "Storage limit exceeded.";
};

export const localizeApiErrorDetail = (
  detail: unknown,
  t: TFunction,
  fallback: string | null = null,
): string | null => {
  const normalized = normalizeApiErrorDetail(detail, fallback);
  if (!normalized) {
    return normalized;
  }

  const i18nKey = KNOWN_ERROR_I18N_KEYS[normalized];
  if (i18nKey) {
    return t(i18nKey);
  }

  return normalized;
};
